import {
  call,
  cancelled,
  delay,
  put,
  race,
  select,
  take,
  takeLatest
} from 'redux-saga/effects';

import {
  downloadCSVFIle,
  transformCSVHistoryData,
  transformData,
  transformHistoryRowData
} from 'api/helpers/people';
import people from 'api/people';

import { StatusTypes } from 'const';
import {
  FILE_UPLOAD_COMPLETE,
  FILE_UPLOAD_START,
  HIDE_SPINNER,
  SHOW_SPINNER
} from 'redux/slices/people';

import { Toast } from 'ui/components';
import i18next from 'i18next';
import moment from 'moment';

import {
  CHANGE_USER_ROLE,
  CREATE_ACCOUNT,
  DELETE_ACCOUNT,
  DOWNLOAD_CSV,
  DOWNLOAD_USERS_LIST,
  FETCH_CSV_HISTORY,
  FETCH_SAMPLE_CSV,
  GET_ALL_ACCOUNTS,
  GET_COMPANIES_LIST,
  GET_ROW_ID_STATUS,
  POST_CREATE_ACCOUNTS_CSV,
  RESEND_EMAIL,
  STOP_FILE_HISTORY_POLLING,
  UPDATE_ACCOUNT
} from './actions/people';
import { MODAL_CLOSE } from '../slices/modals';
import { TOGGLE_SIDEBAR } from '../slices/sidebar';
import { SIGNOUT } from './actions/authentication';

function* postCreateAccountsCSVFile({ payload }) {
  try {
    yield put(FILE_UPLOAD_START());
    const { data } = yield call(people.postCreateAccountsCSVFile, {
      ...payload
    });

    yield put({ type: POST_CREATE_ACCOUNTS_CSV._SUCCESS, payload: data });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: POST_CREATE_ACCOUNTS_CSV._ERROR
    });
    console.log({ err });
    const errorString =
      err.response?.data?.message ||
      i18next.t('create_account_toast_error_message');
    Toast.show(Toast.TYPE.ERROR, errorString);
  } finally {
    yield put(FILE_UPLOAD_COMPLETE());
  }
}

function* postCSVFileWatcher() {
  yield takeLatest(
    POST_CREATE_ACCOUNTS_CSV._REQUEST,
    postCreateAccountsCSVFile
  );
}

function* getAllAccounts({ payload }) {
  try {
    yield put(SHOW_SPINNER());
    const { accessibleRoles } = yield select(state => ({
      accessibleRoles: state?.authentication?.accessibleRoles
    }));

    const {
      selectedRoles,
      searchPattern,
      selectedStatusOptions,
      sortBy,
      sortDirection
    } = yield select(state => state.people);

    const response = yield call(people.getAllAccountsApi, {
      ...payload,
      roles: selectedRoles?.length > 0 ? selectedRoles : accessibleRoles,
      userStatus:
        selectedStatusOptions?.length > 0
          ? selectedStatusOptions
          : [StatusTypes.ENABLED, StatusTypes.DISABLED, StatusTypes.PENDING],
      email: searchPattern,
      sortBy,
      sortDirection
    });

    const transformedData = transformData(response?.data);
    yield put({ type: GET_ALL_ACCOUNTS._SUCCESS, payload: transformedData });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: GET_ALL_ACCOUNTS._ERROR });
  } finally {
    yield put(HIDE_SPINNER());
  }
}

function* getAllAccountsWatch() {
  yield takeLatest(GET_ALL_ACCOUNTS._REQUEST, getAllAccounts);
}

function* createAccount({ payload }) {
  try {
    console.log(payload);
    yield call(people.createAccountPartner, payload);

    yield put({ type: CREATE_ACCOUNT._SUCCESS, payload });
    Toast.show(
      Toast.TYPE.SUCCESS,
      i18next.t('create_account_toast_success_message', {
        email: payload.email
      })
    );
    const { pageNo, pageSize } = yield select(state => ({
      pageNo: state?.people.pageNo,
      pageSize: state?.people.pageSize
    }));
    yield put(TOGGLE_SIDEBAR());
    yield put({
      type: GET_ALL_ACCOUNTS._REQUEST,
      payload: { pageNo, pageSize }
    });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: CREATE_ACCOUNT._ERROR
    });
    Toast.show(
      Toast.TYPE.ERROR,
      err.response?.data?.message ||
        i18next.t('create_account_toast_error_message')
    );
  }
}

function* createAccountWatch() {
  yield takeLatest(CREATE_ACCOUNT._REQUEST, createAccount);
}

function* updateAccountStatus({ payload }) {
  try {
    const response = yield call(people.updateAccountStatusApiCall, payload);

    yield put({
      type: UPDATE_ACCOUNT._SUCCESS,
      payload: response.data
    });

    if (response?.data && Array.isArray(response?.data)) {
      response.data.forEach(p => {
        if (p.updateSuccessful) {
          Toast.show(
            Toast.TYPE.SUCCESS,
            i18next.t('update_account_status_toast_success_message', {
              email: p.email
            })
          );
        }
      });
    }

    const { pageNo, pageSize } = yield select(state => ({
      pageNo: state?.people.pageNo,
      pageSize: state?.people.pageSize
    }));

    yield put({
      type: GET_ALL_ACCOUNTS._REQUEST,
      payload: { pageNo, pageSize }
    });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: UPDATE_ACCOUNT._ERROR
    });
    Toast.show(
      Toast.TYPE.ERROR,
      i18next.t('update_account_status_toast_error_message')
    );
  }
}

function* updateAccountStatusWatch() {
  yield takeLatest(UPDATE_ACCOUNT._REQUEST, updateAccountStatus);
}

function* fetchSampleCSV() {
  try {
    const resp = yield call(people.fetchSampleCSVApiCall);
    if (resp.data) {
      downloadCSVFIle(resp.data);
    }
    yield put({ type: FETCH_SAMPLE_CSV._SUCCESS });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: FETCH_SAMPLE_CSV._ERROR });
  }
}

function* fetchSampleCSVWatch() {
  yield takeLatest(FETCH_SAMPLE_CSV._REQUEST, fetchSampleCSV);
}

function* deleteAccount({ payload }) {
  try {
    yield put(SHOW_SPINNER());
    console.log(payload);
    const { cognitoIds } = payload;

    const { data } = yield call(people.deleteAccountApiCall, {
      cognitoIds
    });

    yield put({ type: DELETE_ACCOUNT._SUCCESS, payload: data });

    const { pageNo, pageSize } = yield select(state => ({
      pageNo: state?.people.pageNo,
      pageSize: state?.people.pageSize
    }));
    yield put({
      type: GET_ALL_ACCOUNTS._REQUEST,
      payload: { pageNo, pageSize }
    });

    yield put({
      type: DELETE_ACCOUNT._SUCCESS
    });
    Toast.show(
      Toast.TYPE.SUCCESS,
      i18next.t('delete_account_toast_success_message', {
        length: cognitoIds?.length,
        account: cognitoIds?.length > 1 ? 'accounts' : 'account'
      })
    );
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: DELETE_ACCOUNT._ERROR
    });
    Toast.show(
      Toast.TYPE.ERROR,
      i18next.t('delete_account_toast_error_message')
    );
  } finally {
    yield put(HIDE_SPINNER());
  }
}

function* deleteAccountWatch() {
  yield takeLatest(DELETE_ACCOUNT._REQUEST, deleteAccount);
}

function* downloadCSVFile({ payload }) {
  try {
    const resp = yield call(people.downloadCSVApiCall, {
      ...payload
    });

    if (resp.data) {
      downloadCSVFIle(resp.data, payload.fileName);
    }

    yield put({ type: DOWNLOAD_CSV._SUCCESS, payload });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: DOWNLOAD_CSV._ERROR });
  }
}

function* downloadCSVFileWatch() {
  yield takeLatest(DOWNLOAD_CSV._REQUEST, downloadCSVFile);
}
function* fetchCSVHistory() {
  const { pageNo, pageSize } = yield select(state => ({
    pageNo: state?.people.pageNo,
    pageSize: state?.people.pageSize
  }));
  let stopPolling = false;
  let countMaxPolling = 0;
  try {
    while (!stopPolling) {
      const { data } = yield call(people.getCSVHistory);
      const transformedData = transformCSVHistoryData(data);
      yield put({ type: FETCH_CSV_HISTORY._SUCCESS, payload: transformedData });
      const inProgressList = data?.filter(
        row => row.fileStatus === 'INPROGRESS'
      );
      if (inProgressList.length === 0) {
        stopPolling = true;
        yield call(getAllAccounts, { payload: { pageNo, pageSize } });
      }
      countMaxPolling += 1;
      if (countMaxPolling === 5) {
        stopPolling = true;
      }
      yield delay(5000);
    }
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: FETCH_CSV_HISTORY._ERROR });
  } finally {
    if (yield cancelled()) {
      // eslint-disable-next-line no-console
      console.log('sagas > people > history', 'History Polling Cancelled');
    }
  }
}

function* fetchCSVHistoryWatch() {
  while (true) {
    const action = yield take(FETCH_CSV_HISTORY._REQUEST);

    yield race({
      task: call(fetchCSVHistory, action),
      cancel: take(STOP_FILE_HISTORY_POLLING._REQUEST)
    });
  }
}

function* getRowIDStatus({ payload }) {
  try {
    const response = yield call(people.getRowIdResponse, {
      ...payload
    });

    const transformedData = transformHistoryRowData(response.data);

    yield put({ type: GET_ROW_ID_STATUS._SUCCESS, payload: transformedData });
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({ type: GET_ROW_ID_STATUS._ERROR });
  }
}

function* getRowIDStatusWatch() {
  yield takeLatest(GET_ROW_ID_STATUS._REQUEST, getRowIDStatus);
}

function* changeUserRole({ payload }) {
  const { selectedRow } = payload;
  try {
    const response = yield call(people.changeUserRole, {
      ...payload
    });

    if (response.data === 'Role successfully updated') {
      yield put({
        type: CHANGE_USER_ROLE._SUCCESS
      });
      Toast.show(
        Toast.TYPE.SUCCESS,
        i18next.t('change_user_role_toast_success_message', {
          email: selectedRow[0]?.email
        })
      );
      const { pageNo, pageSize } = yield select(state => ({
        pageNo: state?.people.pageNo,
        pageSize: state?.people.pageSize
      }));
      yield put({
        type: GET_ALL_ACCOUNTS._REQUEST,
        payload: { pageNo, pageSize }
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('changeUserRole', err);
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: CHANGE_USER_ROLE._ERROR
    });
    Toast.show(
      Toast.TYPE.ERROR,
      i18next.t('change_user_role_toast_error_message', {
        email: selectedRow[0]?.email
      })
    );
  }
}

function* changeUserRoleWatch() {
  yield takeLatest(CHANGE_USER_ROLE._REQUEST, changeUserRole);
}

function* resendEmailAPICall({ payload }) {
  try {
    const cognitoId = payload;

    yield call(people.resendEmail, cognitoId);

    yield put({
      type: RESEND_EMAIL._SUCCESS
    });
    Toast.show(
      Toast.TYPE.SUCCESS,
      i18next.t('resend_email_toast_success_message')
    );
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: RESEND_EMAIL._ERROR
    });
    Toast.show(Toast.TYPE.ERROR, i18next.t('resend_email_toast_error_message'));
  }
}

function* resendEmailWatch() {
  yield takeLatest(RESEND_EMAIL._REQUEST, resendEmailAPICall);
}

function* getCompaniesList() {
  try {
    const { data } = yield call(people.getCompaniesList);
    console.log({ data });
    yield put({
      type: GET_COMPANIES_LIST._SUCCESS,
      payload:
        data && Array.isArray(data)
          ? data.map((res, index) => ({
              value: res.companyId || `empty${index}`,
              label: res.desc || `empty${index}`
            }))
          : []
    });
  } catch (err) {
    console.log(err);
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    yield put({
      type: GET_COMPANIES_LIST._ERROR
    });
  }
}
function* getCompaniesListWatch() {
  yield takeLatest(GET_COMPANIES_LIST._REQUEST, getCompaniesList);
}

function* downloadReportPeoplePage({ payload }) {
  try {
    const selectedEndDate = moment(payload?.period?.[1]);
    selectedEndDate.set('hour', 23);
    selectedEndDate.set('minute', 59);
    selectedEndDate.set('seconds', 59);
    const resp = yield call(people.downloadPeopleFile, {
      startDate: `${moment(payload.period?.[0]).format(
        'YYYY-MM-DDTHH:mm:ss'
      )}Z`,
      endDate: `${selectedEndDate.format('YYYY-MM-DDTHH:mm:ss')}Z`
    });
    if (resp.data) {
      downloadCSVFIle(resp.data, payload.fileName);
    }
    yield put({ type: DOWNLOAD_USERS_LIST._SUCCESS });
    yield put(MODAL_CLOSE());
    Toast.show(
      Toast.TYPE.SUCCESS,
      i18next.t('file_downloaded_toast_success_message')
    );
  } catch (err) {
    if (err?.response?.status === 401) {
      yield put({ type: SIGNOUT._REQUEST });
    }
    Toast.show(
      Toast.TYPE.ERROR,
      i18next.t('file_downloaded_toast_error_message')
    );
    yield put(MODAL_CLOSE());
    yield put({ type: DOWNLOAD_USERS_LIST._ERROR });
  }
}

function* downloadReportPeoplePageWatcher() {
  yield takeLatest(DOWNLOAD_USERS_LIST._REQUEST, downloadReportPeoplePage);
}

export default [
  postCSVFileWatcher(),
  createAccountWatch(),
  getAllAccountsWatch(),
  updateAccountStatusWatch(),
  fetchSampleCSVWatch(),
  deleteAccountWatch(),
  downloadCSVFileWatch(),
  fetchCSVHistoryWatch(),
  getRowIDStatusWatch(),
  changeUserRoleWatch(),
  resendEmailWatch(),
  getCompaniesListWatch(),
  downloadReportPeoplePageWatcher()
];
